import React, { useEffect, useState } from "react";
import { Plus, ArrowLeftRight } from "lucide-react";
import { toast } from "../ui/Toast";

// Manager view: initiate an internal transfer for a report and see one's own
// initiated transfers. Managers CANNOT approve/reject (admin-only). from_* are
// server-snapshotted at create, so the form never collects them. Two pickers:
// employee (/api/performance/team) and to_manager (/transfers/managers).

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const STATUS_STYLE = {
  pending:   "text-amber-400 bg-amber-500/10 border-amber-500/25",
  completed: "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/25",
  rejected:  "text-red-400 bg-red-500/10 border-red-500/20",
};
const STATUS_FALLBACK = "text-white/30 bg-white/5 border-white/10";
const STATUS_LABEL = { pending: "Pending", completed: "Completed", rejected: "Rejected" };
const TYPE_OPTS = [["department", "Department"], ["reporting_line", "Reporting Line"], ["location", "Location"], ["role", "Role"]];
const TYPE_LABEL = Object.fromEntries(TYPE_OPTS);

const fmtDate = (d) => (d ? String(d).slice(0, 10) : "—");
const val = (v) => (v === null || v === undefined || v === "" ? "—" : v);
const statusBadge = (s) => (
  <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLE[s] || STATUS_FALLBACK}`}>{STATUS_LABEL[s] || s || "—"}</span>
);

const EMPTY_FORM = {
  employee_id: "", transfer_type: "department", to_department: "", to_manager_id: "",
  to_title: "", effective_date: "", reason: "",
};

export default function ManagerTransferView() {
  const [transfers, setTransfers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [managers, setManagers]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);

  const token = localStorage.getItem("token");
  const authH = { Authorization: `Bearer ${token}` };
  const jsonH = { ...authH, "Content-Type": "application/json" };

  const loadTransfers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/performance/transfers/team`, { headers: authH });
      const data = res.ok ? await res.json() : [];
      setTransfers(Array.isArray(data) ? data : []);
    } catch { setTransfers([]); }
    finally { setLoading(false); }
  };

  const loadPickers = async () => {
    try {
      const [er, mr] = await Promise.all([
        fetch(`${API_BASE}/api/performance/team`, { headers: authH }),
        fetch(`${API_BASE}/api/performance/transfers/managers`, { headers: authH }),
      ]);
      const ed = er.ok ? await er.json() : [];
      const md = mr.ok ? await mr.json() : [];
      const earr = Array.isArray(ed) ? ed : (ed?.data || []);
      setEmployees(earr.map(r => ({ id: r.user_id, name: r.name || r.user_id })).filter(e => e.id));
      setManagers((Array.isArray(md) ? md : []).map(m => ({ id: m.user_id, name: m.name || m.user_id })).filter(m => m.id));
    } catch { setEmployees([]); setManagers([]); }
  };

  useEffect(() => { loadTransfers(); loadPickers(); }, []); // eslint-disable-line

  const submitCreate = async () => {
    if (!form.employee_id) { toast.error("Select an employee"); return; }
    setSaving(true);
    try {
      const body = {
        employee_id: form.employee_id,
        transfer_type: form.transfer_type,
        to_department: form.to_department || null,
        to_manager_id: form.to_manager_id || null,
        to_title: form.to_title || null,
        effective_date: form.effective_date || null,
        reason: form.reason || null,   // OPTIONAL (unlike Career's required justification)
      };
      const res = await fetch(`${API_BASE}/api/performance/transfers`, {
        method: "POST", headers: jsonH, body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success("Transfer submitted — awaiting HR decision");
      setForm(EMPTY_FORM); setShowCreate(false);
      await loadTransfers();
    } catch { toast.error("Failed to submit transfer"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ArrowLeftRight size={16} className="text-[#00ff90]" />
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Transfers</span>
        </div>
        <button onClick={() => setShowCreate(s => !s)}
          className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
          <Plus size={12} /> New Transfer
        </button>
      </div>

      {showCreate && (
        <div className={`${card} p-5 space-y-4`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Employee</label>
              <select className={inputCls} value={form.employee_id}
                onChange={e => setForm(f => ({ ...f, employee_id: e.target.value }))}>
                <option value="">Select a report…</option>
                {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Transfer Type</label>
              <select className={inputCls} value={form.transfer_type}
                onChange={e => setForm(f => ({ ...f, transfer_type: e.target.value }))}>
                {TYPE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>To Department</label>
              <input className={inputCls} value={form.to_department}
                onChange={e => setForm(f => ({ ...f, to_department: e.target.value }))} />
            </div>
            <div>
              <label className={labelCls}>To Manager</label>
              <select className={inputCls} value={form.to_manager_id}
                onChange={e => setForm(f => ({ ...f, to_manager_id: e.target.value }))}>
                <option value="">Select a manager…</option>
                {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>To Title</label>
              <input className={inputCls} value={form.to_title}
                onChange={e => setForm(f => ({ ...f, to_title: e.target.value }))} />
            </div>
            <div>
              <label className={labelCls}>Effective Date</label>
              <input type="date" className={inputCls} value={form.effective_date}
                onChange={e => setForm(f => ({ ...f, effective_date: e.target.value }))} />
            </div>
            <div className="md:col-span-2">
              <label className={labelCls}>Reason (optional)</label>
              <textarea className={inputCls} rows={2} value={form.reason}
                onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} />
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-1 border-t border-white/5">
            <button onClick={() => { setShowCreate(false); setForm(EMPTY_FORM); }}
              className="text-xs px-4 py-2 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
            <button onClick={submitCreate} disabled={saving || !form.employee_id}
              className="text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
              {saving ? "Submitting…" : "Submit"}
            </button>
          </div>
        </div>
      )}

      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">My Transfers</span>
        </div>
        <div className="divide-y divide-white/5">
          {loading ? (
            Array.from({ length: 2 }).map((_, i) => <div key={i} className="px-5 py-4"><div className="h-12 rounded-lg bg-white/5 animate-pulse" /></div>)
          ) : transfers.length === 0 ? (
            <p className="px-5 py-8 text-white/20 text-sm italic text-center">No transfers submitted.</p>
          ) : transfers.map(t => (
            <div key={t.id} className="flex items-start justify-between gap-3 px-5 py-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <p className="text-sm text-white/80 font-medium">{t.employee_name || t.employee_id}</p>
                  <span className="text-xs text-white/30 border border-white/10 px-2 py-0.5 rounded-full">{TYPE_LABEL[t.transfer_type] || t.transfer_type}</span>
                  {statusBadge(t.status)}
                </div>
                <p className="text-xs text-white/40">
                  Dept: {val(t.from_department)} → {val(t.to_department)} · Mgr: {val(t.from_manager_name)} → {val(t.to_manager_name)} · Title: {val(t.from_title)} → {val(t.to_title)}
                </p>
                <p className="text-xs text-white/25 mt-0.5">Effective: {fmtDate(t.effective_date)}</p>
              </div>
              <div className="shrink-0">
                {t.status === "pending" && (
                  <span className="text-xs px-3 py-1.5 rounded-lg border border-amber-500/20 bg-amber-500/5 text-amber-400/70">Awaiting HR decision</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
